import { FieldError, useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { yupResolver } from "@hookform/resolvers/yup";
import { mdiArrowRight, mdiCheckCircleOutline, mdiLoading } from "@mdi/js";
import Icon from "@mdi/react";
import * as Yup from "yup";

import AutoComplete from "components/AutoComplete";
import Button from "components/Button";
import useDeviceType from "hooks/useDeviceType";
import { IOption } from "models/Form";
import { SignUpUserFormData } from "models/User";
import { AppDispatch } from "store";
import { loginUser } from "store/auth/actions";
import { getCurrentUserAPI, updateAccountAPI } from "services/api/user";
import styles from "./style.module.scss";
import { useAnalytics } from "hooks/useAnalytics";
import useHubspot from "hooks/useHubspot";
import { showErrorMessage } from "helpers/toastMessage";
import { getSignupErrorMessage } from "../../helpers";

interface IProps {
  basicFormData?: SignUpUserFormData;
}

const SIGN_UP_REASONS: IOption[] = [
  {
    id: 0,
    label: "Load Board",
    value: "load_board"
  },
  {
    id: 1,
    label: "HaulPay Factoring",
    value: "haulpay_factoring"
  },
  {
    id: 2,
    label: "Digital Payments",
    value: "digital_payments"
  }
];

const confirmationSectionSchema = Yup.object<
  Partial<{
    reasonForSignUp: IOption[];
  }>
>({
  reasonForSignUp: Yup.array()
    .of(
      Yup.object().shape({
        id: Yup.string(),
        label: Yup.string(),
        value: Yup.string()
      })
    )
    .required("This Field is required")
    .min(1, "This Field is required")
});

export default function ConfirmSection({ basicFormData }: Readonly<IProps>) {
  const dispatch = useDispatch<AppDispatch>();
  const { isDesktop } = useDeviceType();
  const { trackAllEvents } = useAnalytics();
  const { trackSignUp } = useHubspot();

  const {
    control,
    formState: { errors, isSubmitting },
    handleSubmit
  } = useForm<
    Partial<{
      reasonForSignUp: IOption[];
    }>
  >({
    resolver: yupResolver(confirmationSectionSchema)
  });

  const onSubmit = async (data: Partial<{ reasonForSignUp: IOption[] }>) => {
    const { email, password } = basicFormData ?? {};
    const selectedReasons = data.reasonForSignUp ?? [];
    let currentUser: any;

    try {
      await dispatch(
        loginUser({
          username: email ?? "",
          password: password ?? "",
          grant_type: "password",
          client_id: "yzxsFZJYZMxi5kLh21ZZklGYzSuu9zxNO6miNcN8"
        })
      ).unwrap();

      const response = await getCurrentUserAPI();
      currentUser = response.data;
      const reasonsForSignUp = selectedReasons.map((item) => item.value);

      await updateAccountAPI(currentUser.type, currentUser[currentUser.type], {
        reason_for_sign_up: reasonsForSignUp
      });
    } catch (error) {
      showErrorMessage(getSignupErrorMessage(error));
      return;
    }

    try {
      if (email) {
        const reasonLabels = selectedReasons.map((item) => item.label).filter(Boolean);
        const trackingData: any = {
          userType: currentUser.type,
          signUpDate: new Date().toISOString(),
          userId: currentUser.id.toString()
        };
        if (reasonLabels.length > 0) {
          trackingData["hp_interested_in_at_signup"] = reasonLabels.join(";");
        }
        trackSignUp(email, trackingData);
      }
      trackAllEvents("auth_signup_go_to_dashboard");
    } catch {
      // Ignore analytics errors after signup succeeds.
    }
  };

  return (
    <form className={styles.confirmWrapper} onSubmit={handleSubmit(onSubmit)}>
      <div className={styles.congratulations}>
        {isDesktop ? (
          <>
            <Icon path={mdiCheckCircleOutline} size={3.5} className="text-success" />
            <h4 className="font-extrabold">Congratulations!</h4>
          </>
        ) : (
          <h3 className={styles.thankYou}>Thank you for confirmation!</h3>
        )}

        <AutoComplete
          name="reasonForSignUp"
          label="&nbsp;Why did you sign up?"
          options={SIGN_UP_REASONS}
          control={control}
          error={errors.reasonForSignUp as FieldError}
          className="my-4"
          data-cy="signup-equipment-type-select"
          isMulti
        />

        <span className="mobile:text-sm">
          Please select your reason for signing up to enter the app.
        </span>
      </div>

      <Button
        type="submit"
        size="lg"
        suffixNode={
          !isDesktop && (
            <span className={styles.arrowRightIcon}>
              <Icon path={mdiArrowRight} className="m-auto" />
            </span>
          )
        }
        className="desktop:!px-8 desktop:!w-fit"
        data-cy="signup-confirm-button"
        disabled={isSubmitting}>
        {isSubmitting ? (
          <Icon path={mdiLoading} size={1.5} spin={1} />
        ) : isDesktop ? (
          "Go To Dashboard"
        ) : (
          "GO TO DASHBOARD"
        )}
      </Button>
    </form>
  );
}
